import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { PhoneNumber } from '@common/users/models/phone-number.model';
import { User } from '@common/users/models/user.model';
import { UserAppRole } from '@common/users/types/user-app-role.type';
import { ApiService } from '@portal-core/auth/services/api.service';
import { DashboardItem } from '@portal-core/dashboard/models/dashboard-item.model';
import { MadCapImage } from '@portal-core/data/common/models/madcap-image.model';
import { UserSubscription } from '@portal-core/users/models/user-subscription.model';
import { UserEdit } from '@portal-core/users/models/user-edit.model';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable, map } from 'rxjs';

@Service()
export class UsersApiService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  @SharePendingRequest()
  getCurrentUser$(): Observable<User> {
    return this.http.get<User>(`${this.apiService.centralApiBaseUri}/api/users/self`);
  }

  @SharePendingRequest()
  getUserById$(userId: string): Observable<User> {
    return this.http.get<User>(`${this.apiService.centralApiBaseUri}/api/users/${userId}`);
  }

  @SharePendingRequest()
  getUserByName$(userName: string): Observable<User> {
    return this.http.get<User>(`${this.apiService.centralApiBaseUri}/api/users/${userName}`);
  }

  @SharePendingRequest()
  getDashboardSettingsByLicenseId$(licenseId: number): Observable<DashboardItem[]> {
    // The endpoint returns the settings as a JSON string (LicenseUser.HomeDashBoardSettings), not an array.
    return this.http.get<string>(`${this.apiService.centralApiBaseUri}/api/users/me/dashboard/${licenseId}`).pipe(
      map(dashboardSettings => typeof dashboardSettings === 'string' ? JSON.parse(dashboardSettings) : [])
    );
  }

  @SharePendingRequest()
  getDashboardSettingsByProjectId$(projectId: number): Observable<DashboardItem[]> {
    // The endpoint returns the settings as a JSON string (ProjectLicenseUser.ProjectDashBoardSettings), not an array.
    return this.http.get<string>(`${this.apiService.centralApiBaseUri}/api/users/me/projectdashboard/${projectId}`).pipe(
      map(dashboardSettings => typeof dashboardSettings === 'string' ? JSON.parse(dashboardSettings) : [])
    );
  }

  updateUserSettings$(user?: Partial<UserEdit>, avatarFile?: File): Observable<UserEdit> {
    const formData = new FormData();
    formData.append('User', user ? JSON.stringify(user) : null);

    if (typeof avatarFile !== 'undefined') {
      formData.append('AvatarFile', avatarFile);
    }

    formData.append('IsAvatarSet', JSON.stringify(avatarFile !== null ? true : false));

    return this.http.put<UserEdit>(`${this.apiService.centralApiBaseUri}/api/users/${user.Id}`, formData);
  }

  disableGettingStartedSetting$(userId: string): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/DisableGettingStarted`, null);
  }

  saveNewPhoneNumber$(userId: string, phoneNumber: string, label: string): Observable<User> {
    return this.http.post<User>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/PhoneNumber/${phoneNumber}?label=${label}`, null);
  }

  updatePhoneNumber$(userId: string, phoneNumberId: number, phoneNumber: string, label: string): Observable<PhoneNumber> {
    return this.http.put<PhoneNumber>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/PhoneNumber/${phoneNumberId}?label=${label}`, { value: phoneNumber });
  }

  updateEmail$(currentEmail: string, newEmail: string): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/users/ChangeUsername` +
      `?currentUsername=${encodeURIComponent(currentEmail)}&newUsername=${encodeURIComponent(newEmail)}`, null);
  }

  updateUserAvatar$(userId: string, avatarImage: FormData): Observable<MadCapImage> {
    return this.http.post<MadCapImage>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/Avatar`, avatarImage);
  }

  deleteUserAvatar$(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/Avatar`);
  }

  @SharePendingRequest()
  getSubscriptions$(): Observable<UserSubscription[]> {
    return this.http.get<UserSubscription[]>(`${this.apiService.centralApiBaseUri}/api/users/subscriptions`);
  }

  @SharePendingRequest()
  getWhatsNew$(): Observable<MadCloudResult<string>> {
    return this.http.get<MadCloudResult<string>>(`${this.apiService.centralApiBaseUri}/api/users/whatsnew`);
  }

  @SharePendingRequest()
  getUsersPage$(filter: PageFilter): Observable<Page<User>> {
    return this.http.post<Page<User>>(`${this.apiService.centralApiBaseUri}/api/users/query`, filter);
  }

  updateUserRoles$(userIds: string[], newRoles: UserAppRole[]): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/users/updateUserRole`, {
      userIds, newRoles
    });
  }

  updateLatestVersionViewed$(): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/users/updateLatestVersionViewed`, null);
  }

  resetAccessFailed$(userId: string): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/users/${userId}/ResetAccessFailed`, null);
  }
}
